import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { useLang } from "@/lang";
import { Reveal, Eyebrow } from "@/components/Reveal";

const Services = () => {
  const { t } = useLang();
  const [active, setActive] = useState(0);

  return (
    <section id="services" data-testid="services-section" className="bg-white py-20 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 max-w-3xl sm:mb-20">
          <Reveal>
            <Eyebrow>{t.services.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-bei-navy sm:text-4xl lg:text-5xl">
              {t.services.title}
              <span className="block text-bei-brand">{t.services.titleAccent}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
              {t.services.subtitle}
            </p>
          </Reveal>
        </div>

        <div className="border-t border-slate-200">
          {t.services.items.map((s, i) => {
            const isOpen = active === i;
            return (
              <Reveal key={s.num} delay={i * 0.05}>
                <div className="border-b border-slate-200" data-testid={`service-row-${s.num}`}>
                  <button
                    data-testid={`service-toggle-${s.num}`}
                    onClick={() => setActive(isOpen ? -1 : i)}
                    className="group flex w-full items-center gap-5 py-7 text-left sm:gap-10 sm:py-9"
                  >
                    <span
                      className={`font-mono text-sm font-semibold transition-colors duration-300 ${
                        isOpen ? "text-bei-cyan" : "text-slate-400 group-hover:text-bei-brand"
                      }`}
                    >
                      {s.num}
                    </span>
                    <span
                      className={`flex-1 font-display text-2xl font-bold tracking-tight transition-all duration-300 sm:text-4xl ${
                        isOpen ? "text-bei-brand sm:translate-x-2" : "text-bei-navy group-hover:text-bei-brand group-hover:sm:translate-x-2"
                      }`}
                    >
                      {s.title}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
                        isOpen
                          ? "border-bei-brand bg-bei-brand text-white"
                          : "border-slate-300 text-slate-500 group-hover:border-bei-brand group-hover:text-bei-brand"
                      }`}
                    >
                      <Plus className="h-5 w-5" />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="grid gap-8 pb-10 sm:pl-[4.5rem] lg:grid-cols-2 lg:gap-14">
                          <div>
                            <p className="text-base font-medium leading-relaxed text-slate-700 sm:text-lg">
                              {s.tagline}
                            </p>
                            <ul className="mt-6 space-y-3.5">
                              {s.details.map((d, j) => (
                                <motion.li
                                  key={j}
                                  initial={{ opacity: 0, x: -12 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.15 + j * 0.08, duration: 0.5 }}
                                  className="flex items-start gap-3 text-sm text-slate-600 sm:text-base"
                                >
                                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bei-cyan/15">
                                    <Check className="h-3 w-3 text-bei-brand" />
                                  </span>
                                  {d}
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                          <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="relative hidden overflow-hidden rounded-2xl lg:block"
                          >
                            <img src={s.img} alt={s.title} className="h-64 w-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-bei-navy/60 to-transparent" />
                            <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/90">
                              BEINTEC · {s.num}
                            </span>
                          </motion.div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
